// Pure formatting / validation helpers for the app UI. No DOM, no state, no API.
//
// The formatters the invoice/quote/credit note DOCUMENT is built from live in
// shared/ instead, because the PDF renderer needs the identical implementation
// — they are re-exported here so app code keeps one import surface for
// formatting. Don't reimplement one of them locally: a second copy is how the
// preview came to format dates in the viewer's locale while the PDF used en-GB.
import {
  escapeHtml,
  formatSortCode,
  formatCurrencyPreviewHtml,
  toPreviewMultiline
} from "../../../shared/document-format.js";
import { CURRENCIES, currencyOptions, currencySymbol, formatMoney } from "../../../shared/currencies.js";

export { escapeHtml, formatSortCode, formatCurrencyPreviewHtml, toPreviewMultiline };
export { CURRENCIES, currencyOptions, currencySymbol };

export function toTitleCase(value) {
  return String(value)
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

export function splitNameParts(name) {
  const normalized = String(name || "").trim().replace(/\s+/g, " ");
  if (!normalized) return { firstName: "", lastName: "" };
  const parts = normalized.split(" ");
  if (parts.length === 1) return { firstName: parts[0], lastName: "" };
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" ")
  };
}

export function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());
}

export function getPasswordUpdatedCopy(rawIsoDate) {
  // No date, no claim (issue #503). This used to read users.updated_at, which
  // a name or email change moves as well — so changing your name told you that
  // you had changed your PASSWORD that day, on the one screen somebody opens
  // when they are worried about their account. It reads password_changed_at
  // now, which is null until somebody actually changes one and is deliberately
  // not backfilled: "recently" about a date nobody knows is worse than silence
  // on a security screen.
  if (!rawIsoDate) return "";
  const updated = new Date(rawIsoDate);
  if (Number.isNaN(updated.getTime())) return "";

  const now = new Date();
  const diffMs = now.getTime() - updated.getTime();
  if (diffMs >= 0 && diffMs < 60 * 1000) {
    return "Last updated just now";
  }
  if (diffMs >= 60 * 1000 && diffMs < 60 * 60 * 1000) {
    const minutes = Math.max(1, Math.floor(diffMs / (60 * 1000)));
    return `Last updated ${minutes} min ago`;
  }

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfUpdated = new Date(updated.getFullYear(), updated.getMonth(), updated.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const dayDiff = Math.floor((startOfToday - startOfUpdated) / msPerDay);
  const timeLabel = formatTimeOfDay(updated);

  if (dayDiff <= 0) return `Last updated today at ${timeLabel}`;
  if (dayDiff === 1) return `Last updated yesterday at ${timeLabel}`;

  const formatted = updated.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
  return `Last updated ${formatted}`;
}

// Currency defaults to GBP so every existing single-argument call site keeps
// today's behaviour.
//
// This was its own `Intl.NumberFormat`, byte-identical in output to
// `formatMoney` in `shared/currencies.js` — proved across every supported
// currency in `test/extra-summaries.test.mjs`, which is what let the compose
// summary sentences move into `shared/` without changing a character of what
// the web renders. Two identical formatters is the drift `shared/` exists to
// prevent, so this one delegates rather than repeating it.
export function formatCurrency(rawValue, currency = "GBP") {
  return formatMoney(rawValue, currency);
}

/**
 * The same, from the integer pence the API sends. Every amount on the wire is
 * `…Pence` (iOS map, ticket 06), so this is where money becomes a number
 * again — once, at the point it is shown.
 *
 * @param {number|null|undefined} pence
 * @param {string} [currency]
 * @returns {string}
 */
export function formatPence(pence, currency = "GBP") {
  return formatCurrency((Number(pence) || 0) / 100, currency);
}





// "£25,650.00" for a single currency, "£25,650.00 + $1,200.00" when mixed.
// GBP leads, the rest alphabetical; an empty bucket shows £0.00 (as before).
//
// The amounts are integer PENCE, like every other figure that reaches a screen
// (iOS map, ticket 06). They used to be pounds here alone, because the tiles
// divided by 100 on the way in; shared/document-list-rules.js now totals them,
// and it totals in pence like the rest of shared/.
export function formatBucketAmount(bucket) {
  const entries = Object.entries(bucket.amounts || {});
  if (!entries.length) return formatPence(0);
  entries.sort(([a], [b]) => (a === "GBP" ? -1 : b === "GBP" ? 1 : a.localeCompare(b)));
  return entries.map(([cur, pence]) => formatPence(pence, cur)).join(" + ");
}

// "9am", "9:30am", "2pm" — and "midday" / "midnight" for the two a 12-hour
// clock can't say without ambiguity.
//
// 24-hour reads as a machine talking: "09:00" makes a person stop and work out
// whether that's morning or night, which is the one thing a send time has to
// be obvious about. Minutes appear only when there are any, so a whole hour
// stays two characters instead of five.
export function formatTimeOfDay(date, zone = {}) {
  // formatToParts THROWS on an invalid date rather than returning something
  // useless, so this has to come first — the checks below never get a chance.
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-GB", {
    ...zone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date);
  const value = (type) => Number(parts.find((p) => p.type === type)?.value ?? NaN);
  const hours = value("hour");
  const minutes = value("minute");
  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return "";

  if (minutes === 0 && hours === 0) return "midnight";
  if (minutes === 0 && hours === 12) return "midday";

  const suffix = hours < 12 ? "am" : "pm";
  // 0 and 12 both show as 12 on a 12-hour clock; every other hour is h % 12.
  const display = hours % 12 === 0 ? 12 : hours % 12;
  return minutes === 0
    ? `${display}${suffix}`
    : `${display}:${String(minutes).padStart(2, "0")}${suffix}`;
}

// "Today at 9am", "Yesterday at 2:30pm", "5 Sep 2026 at 11am" — the timestamp
// on an activity line.
//
// One copy. This was three byte-identical private functions (the invoice and
// quote status drawers, and Team), which is how a change to one of them
// becomes a difference between screens rather than a change to the app.
//
// The reader's own timezone is right here, unlike a scheduled send: these are
// records of something that already happened, and "when did that happen" is
// asked from where you're sitting.
export function formatActivityTimestamp(isoStr) {
  if (!isoStr) return "";
  const d = new Date(isoStr);
  if (Number.isNaN(d.getTime())) return "";
  const time = formatTimeOfDay(d);
  const now = new Date();
  // Calendar days, NOT elapsed milliseconds. "Today" and "Yesterday" are
  // positions on a calendar, and `Math.floor((now - d) / dayMs)` gets two
  // ordinary cases wrong: an event stamped seconds in the future by a server
  // clock a touch ahead goes negative and falls through to a full date, and
  // 11pm last night read at 1am this morning is two hours ago, so it claims
  // "Today". Same start-of-day comparison formatRelativeUpdated already uses.
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return `Today at ${time}`;
  if (diffDays === 1) return `Yesterday at ${time}`;
  const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${date} at ${time}`;
}

// File sizes the way the mail preview already writes them ("29 KB" — space
// before the unit, matching the dominant house form). KB below 1MB, one
// decimal of MB above, trailing ".0" dropped so 2MB reads "2 MB" not "2.0 MB".
export function formatFileSize(bytes) {
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} KB`;
  const mb = n / (1024 * 1024);
  const rounded = Math.round(mb * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} MB`;
}
